/**
 * Revision line editor → members (lineage admin polish, after #726).
 *
 * The Create / Edit revision line dialog picks member calibres and sets each
 * one's revision label, years and current flag inline. This module turns the
 * dialog's rows into setCalibreRevision writes. Pure; no Firestore.
 *
 * Curator values are never overwritten silently:
 *   - every row starts from the calibre's STORED values, so a write only
 *     changes what the curator visibly edited;
 *   - at save the calibres are re-read, and a calibre whose revision fields
 *     changed since the dialog opened (or that joined another line) is skipped
 *     and reported, never written over;
 *   - a placeholder keeps its flag and gets no label, years or current;
 *   - AI-stamped years keep the 'ai' stamp only while both years are untouched
 *     (the CalibreRevisionsCard contract).
 */
import type { CustomCalibreEntry } from './firebase';
import type { RevisionFields } from './calibreLineage';
import { parseYearInput } from './calibreLineageLogic';

export interface MemberDraft {
  id: string;
  name: string;
  revisionLabel: string;
  from: string;
  until: string;
  isCurrent: boolean;
  /** Stored; not editable here (set on the calibre's Revisions card). */
  isPlaceholder: boolean;
}

export function draftFromStored(c: CustomCalibreEntry): MemberDraft {
  return {
    id: c.id,
    name: c.name,
    revisionLabel: c.revisionLabel ?? '',
    from: typeof c.activeFromYear === 'number' ? String(c.activeFromYear) : '',
    until: typeof c.activeUntilYear === 'number' ? String(c.activeUntilYear) : '',
    isCurrent: c.isCurrent === true,
    isPlaceholder: c.isLineagePlaceholder === true,
  };
}

/** The revision facts a curator can see — what "changed since you opened this" compares. */
function snapshotKey(c: CustomCalibreEntry): string {
  return JSON.stringify([
    c.calibreLineageId ?? null,
    c.revisionLabel ?? null,
    c.activeFromYear ?? null,
    c.activeUntilYear ?? null,
    c.isCurrent === true,
    c.isLineagePlaceholder === true,
  ]);
}

/** Problems that block Save (bad years); shown on the row. */
export function draftErrors(d: MemberDraft): string[] {
  const out: string[] = [];
  const bad = (raw: string) => raw.trim() !== '' && parseYearInput(raw) === undefined;
  if (bad(d.from) || bad(d.until)) out.push('Years must be four digits.');
  const from = parseYearInput(d.from);
  const until = parseYearInput(d.until);
  if (from !== undefined && until !== undefined && until < from) out.push('Active until is before active from.');
  return out;
}

export interface MemberWrite {
  id: string;
  name: string;
  fields: RevisionFields;
}

export interface MemberPlan {
  writes: MemberWrite[];
  /** Calibres not written, with the reason. */
  skipped: { id: string; name: string; reason: string }[];
}

export function planMemberWrites(input: {
  lineageId: string;
  /** Calibres as the dialog loaded them (edit mode: the line's members; plus every picked calibre). */
  opened: ReadonlyMap<string, CustomCalibreEntry>;
  /** The same calibres re-read at save. A missing entry = deleted since. */
  fresh: ReadonlyMap<string, CustomCalibreEntry>;
  drafts: readonly MemberDraft[];
  removedIds: readonly string[];
}): MemberPlan {
  const { lineageId, opened, fresh, drafts, removedIds } = input;
  const writes: MemberWrite[] = [];
  const skipped: MemberPlan['skipped'] = [];

  const checkFresh = (id: string, name: string): CustomCalibreEntry | null => {
    const now = fresh.get(id);
    const then = opened.get(id);
    if (!now) {
      skipped.push({ id, name, reason: 'no longer exists' });
      return null;
    }
    if (!then || snapshotKey(now) !== snapshotKey(then)) {
      skipped.push({ id, name, reason: 'was changed elsewhere since this dialog opened — reopen to see its values' });
      return null;
    }
    if (now.calibreLineageId && now.calibreLineageId !== lineageId) {
      skipped.push({ id, name, reason: `is in another revision line (${now.calibreLineageId})` });
      return null;
    }
    return now;
  };

  for (const id of removedIds) {
    const now = checkFresh(id, opened.get(id)?.name ?? id);
    if (now?.calibreLineageId === lineageId) writes.push({ id, name: now.name, fields: { calibreLineageId: null } });
  }

  const current: MemberWrite[] = [];
  for (const d of drafts) {
    const now = checkFresh(d.id, d.name);
    if (!now) continue;
    const unchanged =
      now.calibreLineageId === lineageId && JSON.stringify(draftFromStored(now)) === JSON.stringify({ ...d, name: now.name });
    if (unchanged) continue;
    let fields: RevisionFields;
    if (now.isLineagePlaceholder === true) {
      fields = { calibreLineageId: lineageId, isLineagePlaceholder: true };
    } else {
      const activeFromYear = parseYearInput(d.from);
      const activeUntilYear = parseYearInput(d.until);
      const yearsUntouched = activeFromYear === now.activeFromYear && activeUntilYear === now.activeUntilYear;
      fields = {
        calibreLineageId: lineageId,
        revisionLabel: d.revisionLabel,
        activeFromYear,
        activeUntilYear,
        isCurrent: d.isCurrent,
        ...(yearsUntouched && now.activeDatesSource === 'ai'
          ? { activeDatesSource: 'ai' as const, activeDatesConfidence: now.activeDatesConfidence }
          : {}),
      };
    }
    (fields.isCurrent ? current : writes).push({ id: d.id, name: now.name, fields });
  }
  // The current member last: its write un-marks any other current member of the line.
  return { writes: [...writes, ...current], skipped };
}
